import { useEffect, useMemo, useRef, useState } from "react";
import { getProjectCustomFieldSchemas } from "@/lib/projects";
import type { Order } from "@/lib/projects";
import {
  buildLabelIndexFromSchemas,
  computeHoursUsageMeta,
  emptyHoursUsageEntry,
  fetchActualHoursBatch,
  finalizeHoursUsageEntry,
  type HoursUsageEntry,
} from "@/pages/projekte/hoursUsage";

/**
 * Lädt Stundenverbrauch (Ist vs. Kalk.) für Festpreis-Projekte – analog zu
 * `preloadHoursUsageData` in auftraege.js.
 */
export function useHoursUsage(orders: Order[], enabled: boolean): Record<number, HoursUsageEntry> {
  const [usageById, setUsageById] = useState<Record<number, HoursUsageEntry>>({});
  const labelIndexRef = useRef<Map<string, { schemaId: string; propertyUuid: string }> | null>(
    null,
  );
  const resolvedActualRef = useRef<Map<number, HoursUsageEntry>>(new Map());
  const runIdRef = useRef(0);
  const ordersRef = useRef(orders);
  ordersRef.current = orders;
  const orderIdsKey = useMemo(
    () =>
      orders
        .filter((order) => order.id > 0)
        .map((order) => {
          const kalk = (order.kalk_stunden_app || [])
            .map((item) => `${item.lohnart_id}:${item.stunden}`)
            .join("|");
          return `${order.id}:${order.auftragsart_app || ""}:${order.has_app_angebot ? 1 : 0}:${kalk}:${order.nachweis_stunden_app ?? ""}`;
        })
        .sort()
        .join(","),
    [orders],
  );

  useEffect(() => {
    if (!enabled || !orderIdsKey) {
      setUsageById({});
      return;
    }

    let cancelled = false;
    const runId = ++runIdRef.current;

    void (async () => {
      const ordersSnapshot = ordersRef.current;
      if (!ordersSnapshot.length) return;

      if (!labelIndexRef.current) {
        try {
          const schemas = await getProjectCustomFieldSchemas();
          labelIndexRef.current = buildLabelIndexFromSchemas(schemas);
        } catch (err) {
          console.warn("[useHoursUsage] Schemas laden fehlgeschlagen:", err);
          labelIndexRef.current = new Map();
        }
      }

      if (cancelled || runId !== runIdRef.current) return;

      const labelIndex = labelIndexRef.current;
      const initial: Record<number, HoursUsageEntry> = {};
      const toFetch: Order[] = [];

      for (const order of ordersSnapshot) {
        const meta = computeHoursUsageMeta(order, labelIndex);
        const cached = resolvedActualRef.current.get(order.id);
        if (
          cached &&
          cached.eligible === meta.eligible &&
          cached.plannedHours === meta.plannedHours &&
          cached.nachweisPlannedHours === meta.nachweisPlannedHours
        ) {
          initial[order.id] = cached;
          continue;
        }

        if (!meta.eligible) {
          const entry = emptyHoursUsageEntry();
          initial[order.id] = entry;
          resolvedActualRef.current.set(order.id, entry);
          continue;
        }

        const pending: HoursUsageEntry = {
          ...meta,
          actualHours: null,
          percentRaw: null,
          loading: true,
          error: false,
        };
        initial[order.id] = pending;
        toFetch.push(order);
      }

      if (!cancelled && runId === runIdRef.current) {
        setUsageById((prev) => ({ ...prev, ...initial }));
      }

      if (toFetch.length === 0) return;

      const actualByOrder = await fetchActualHoursBatch(toFetch);
      if (cancelled || runId !== runIdRef.current) return;

      const finalizedUpdates: Record<number, HoursUsageEntry> = {};
      for (const order of toFetch) {
        const meta = computeHoursUsageMeta(order, labelIndex);
        const actual = actualByOrder.get(order.id) ?? {
          actualHours: null,
          nachweisHours: null,
          error: true,
        };
        const finalized = finalizeHoursUsageEntry(
          meta,
          actual.actualHours,
          actual.error,
          actual.nachweisHours,
        );
        resolvedActualRef.current.set(order.id, finalized);
        finalizedUpdates[order.id] = finalized;
      }
      setUsageById((prev) => ({ ...prev, ...finalizedUpdates }));
    })();

    return () => {
      cancelled = true;
    };
  }, [orderIdsKey, enabled]);

  return usageById;
}
